import React, { useEffect, useState, useMemo, useCallback } from "react";
import { PiCaretRight } from "react-icons/pi";
import styles from "./FilterSidebar.module.css";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import debounce from "lodash.debounce";
import { BOOLEAN_FILTER_KEYS } from "../utils/filterUtils";

const DEFAULT_PRICE_RANGE = { min: 1, max: 10000 };

const ADVANCED_KIND_WHITELIST = new Set([
  "Сухий корм",
  "Вологий корм",
  "Замінники молока",
  "Вітаміни та дієтичні добавки",
  "Ласощі",
]);

const SHOW_WHEN_NO_KIND_SELECTED = true;

const sectionTitles = {
  categories: "Категорії",
  kinds: "Вид",
  types: "Тип",
  purposes: "Призначення",
  manufacturers: "Виробник",
  countries: "Країна-виробник",
  weights: "Розмір продукту",
  feedClasses: "Клас корму",
  animalAges: "Вік тварини",
  breeds: "Породи",
  mainIngredients: "Основні інгредієнти",
  forSterilized: "Для стерилізованих",
  hypoallergenic: "Гіпоалергенний",
  therapeuticFeed: "Лікувальний корм",
  priceRange: "Ціна, грн",
};

const FilterSidebar = ({
  isOpen,
  onClose,
  products = [],
  filters = {},
  onFilterChange,
  onResetFilters,
  animalCategory = null,
  sheetMode = false,
  hideInternalReset = false,
}) => {
  const [sections, setSections] = useState({
    categories: false,
    kinds: false,
    types: false,
    purposes: false,
    manufacturers: false,
    countries: false,
    weights: false,
    feedClasses: false,
    animalAges: false,
    breeds: false,
    mainIngredients: false,
    forSterilized: false,
    hypoallergenic: false,
    therapeuticFeed: false,
    priceRange: false,
  });

  const initialRange = filters?.priceRange ?? DEFAULT_PRICE_RANGE;
  const [priceInput, setPriceInput] = useState([
    Number(initialRange.min) || DEFAULT_PRICE_RANGE.min,
    Number(initialRange.max) || DEFAULT_PRICE_RANGE.max,
  ]);

  useEffect(() => {
    const r = filters?.priceRange ?? DEFAULT_PRICE_RANGE;
    setPriceInput([
      Number(r.min) || DEFAULT_PRICE_RANGE.min,
      Number(r.max) || DEFAULT_PRICE_RANGE.max,
    ]);
  }, [filters.priceRange]);

  const extractUniqueWithCount = (items) => {
    const counts = {};
    (items || []).filter(Boolean).forEach((item) => {
      counts[item] = (counts[item] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) =>
      String(a[0]).localeCompare(String(b[0]))
    );
  };

  const arrayify = (v) => (Array.isArray(v) ? v : v != null ? [v] : []);
  const isEmpty = (v) => v == null || (Array.isArray(v) && v.length === 0);

  const fieldMap = useMemo(
    () => ({
      categories: (p) =>
        Array.isArray(p.category) ? p.category : [p.category],
      kinds: (p) => [p.kind],
      types: (p) => [p.type],
      purposes: (p) => arrayify(p.purpose),
      manufacturers: (p) => [p.manufacturer],
      countries: (p) => [p.countryOfOrigin],
      weights: (p) => [p.weight],
      feedClasses: (p) => [p.feedClass],
      animalAges: (p) => arrayify(p.animalAge),
      breeds: (p) => (Array.isArray(p.breed) ? p.breed : [p.breed]),
      mainIngredients: (p) =>
        Array.isArray(p.mainIngredients)
          ? p.mainIngredients
          : [p.mainIngredients],
    }),
    []
  );

  const getFilteredProductsExcludingKey = useCallback(
    (excludedKey) => {
      const list = Array.isArray(products) ? products : [];
      const f = filters || {};

      return list.filter((product) => {
        return Object.entries(f).every(([key, selectedValues]) => {
          if (key === excludedKey) return true;

          if (key === "priceRange") {
            if (!selectedValues || typeof selectedValues !== "object") {
              return true;
            }

            const price = Number(product?.price);
            const min = Number(selectedValues.min) || DEFAULT_PRICE_RANGE.min;
            const max = Number(selectedValues.max) || DEFAULT_PRICE_RANGE.max;

            return Number.isFinite(price) && price >= min && price <= max;
          }

          if (BOOLEAN_FILTER_KEYS.includes(key)) {
            if (selectedValues == null) return true;
            return product?.[key] === selectedValues;
          }

          if (isEmpty(selectedValues)) return true;

          const selectedArr = arrayify(selectedValues);
          const productValues = fieldMap[key]?.(product) || [];

          return productValues.some((val) => selectedArr.includes(val));
        });
      });
    },
    [products, filters, fieldMap]
  );

  const availableFilters = useMemo(() => {
    const flat = (arr) => arr.reduce((acc, v) => acc.concat(arrayify(v)), []);

    return {
      kinds: extractUniqueWithCount(
        getFilteredProductsExcludingKey("kinds").map((p) => p.kind)
      ),
      types: extractUniqueWithCount(
        getFilteredProductsExcludingKey("types").map((p) => p.type)
      ),
      purposes: extractUniqueWithCount(
        flat(getFilteredProductsExcludingKey("purposes").map((p) => p.purpose))
      ),
      manufacturers: extractUniqueWithCount(
        getFilteredProductsExcludingKey("manufacturers").map(
          (p) => p.manufacturer
        )
      ),
      countries: extractUniqueWithCount(
        getFilteredProductsExcludingKey("countries").map(
          (p) => p.countryOfOrigin
        )
      ),
      weights: extractUniqueWithCount(
        getFilteredProductsExcludingKey("weights").map((p) => p.weight)
      ),

      feedClasses: extractUniqueWithCount(
        getFilteredProductsExcludingKey("feedClasses").map((p) => p.feedClass)
      ),
      animalAges: extractUniqueWithCount(
        flat(getFilteredProductsExcludingKey("animalAges").map((p) => p.animalAge))
      ),
      breeds: extractUniqueWithCount(
        flat(
          getFilteredProductsExcludingKey("breeds").map((p) =>
            fieldMap.breeds(p)
          )
        )
      ),
      mainIngredients: extractUniqueWithCount(
        flat(
          getFilteredProductsExcludingKey("mainIngredients").map((p) =>
            fieldMap.mainIngredients(p)
          )
        )
      ),
    };
  }, [getFilteredProductsExcludingKey, fieldMap]);

  const availableCategories = useMemo(() => {
    if (animalCategory) return [animalCategory];

    const categories = new Set();

    getFilteredProductsExcludingKey("categories").forEach((product) => {
      const productCategories = Array.isArray(product.category)
        ? product.category
        : [product.category];

      productCategories.filter(Boolean).forEach((category) => {
        categories.add(category);
      });
    });

    (filters.categories ?? []).forEach((category) => {
      if (category) categories.add(category);
    });

    return Array.from(categories).sort((a, b) =>
      String(a).localeCompare(String(b))
    );
  }, [animalCategory, filters.categories, getFilteredProductsExcludingKey]);

  useEffect(() => {
    if (!isOpen) {
      setSections({
        categories: false,
        kinds: false,
        types: false,
        purposes: false,
        manufacturers: false,
        countries: false,
        weights: false,
        feedClasses: false,
        animalAges: false,
        breeds: false,
        mainIngredients: false,
        forSterilized: false,
        hypoallergenic: false,
        therapeuticFeed: false,
        priceRange: false,
      });
    } else {
      setSections((prev) => ({ ...prev, categories: true }));
    }
  }, [isOpen]);

  const handleCheckbox = useCallback(
    (key, value) => {
      const cur = Array.isArray(filters[key]) ? filters[key] : [];
      const next = cur.includes(value)
        ? cur.filter((v) => v !== value)
        : [...cur, value];
      onFilterChange({ [key]: next });
    },
    [filters, onFilterChange]
  );

  const handleTriState = useCallback(
    (key, val) => {
      onFilterChange({ [key]: val });
    },
    [onFilterChange]
  );

  const applyPriceFilter = useCallback(
    (values) => {
      onFilterChange({ priceRange: { min: values[0], max: values[1] } });
    },
    [onFilterChange]
  );

  const debouncedApply = useMemo(
    () => debounce(applyPriceFilter, 150),
    [applyPriceFilter]
  );

  const handleSliderChange = (values) => {
    setPriceInput(values);
    debouncedApply(values);
  };

  const handleSliderAfterChange = (values) => {
    debouncedApply.cancel();
    applyPriceFilter(values);
  };

  useEffect(() => () => debouncedApply.cancel(), [debouncedApply]);

  const resetSections = useCallback(() => {
    setSections({
      categories: false,
      kinds: false,
      types: false,
      purposes: false,
      manufacturers: false,
      countries: false,
      weights: false,
      feedClasses: false,
      animalAges: false,
      breeds: false,
      mainIngredients: false,
      forSterilized: false,
      hypoallergenic: false,
      therapeuticFeed: false,
      priceRange: false,
    });
  }, []);

  const resetAllFilters = () => {
    debouncedApply.cancel();
    setPriceInput([DEFAULT_PRICE_RANGE.min, DEFAULT_PRICE_RANGE.max]);
    resetSections();
    onResetFilters();
  };

  const categoryCounts = useMemo(() => {
    const counts = {};
    const list = getFilteredProductsExcludingKey("categories");
    list.forEach((p) => {
      const cats = Array.isArray(p.category) ? p.category : [p.category];
      cats.forEach((c) => {
        if (c) counts[c] = (counts[c] || 0) + 1;
      });
    });
    return counts;
  }, [getFilteredProductsExcludingKey]);

  const selectedKinds = filters?.kinds ?? [];
  const shouldShowAdvanced =
    selectedKinds.length === 0
      ? SHOW_WHEN_NO_KIND_SELECTED
      : selectedKinds.every((k) => ADVANCED_KIND_WHITELIST.has(k));

  const booleanFilterAvailability = useMemo(
    () => Object.fromEntries(BOOLEAN_FILTER_KEYS.map(key => [
      key, getFilteredProductsExcludingKey(key).some(product => typeof product?.[key] === "boolean"),
    ])),
    [getFilteredProductsExcludingKey]
  );

  const renderSectionHeading = useCallback(
    (key) => (
      <>
        <span className={styles.sectionTitle}>{sectionTitles[key]}</span>
        <PiCaretRight
          className={`${styles.sectionChevron} ${
            sections[key] ? styles.sectionChevronOpen : ""
          }`}
          aria-hidden="true"
        />
      </>
    ),
    [sections]
  );

  const renderArraySection = useCallback(
    (key) => {
      const options = availableFilters[key] || [];
      const hasActiveSelection =
        Array.isArray(filters[key]) && filters[key].length > 0;

      if (options.length === 0 && !hasActiveSelection) return null;

      return (
        <div key={key} className={styles.filterSection}>
          <h4
            onClick={() =>
              setSections((prev) => ({ ...prev, [key]: !prev[key] }))
            }
          >
            {renderSectionHeading(key)}
          </h4>

          {sections[key] &&
            (options.length > 0 ? (
              options.map(([value, count]) => (
                <label key={`${key}-${value}`}>
                  <input
                    type="checkbox"
                    checked={
                      Array.isArray(filters[key]) &&
                      filters[key].includes(value)
                    }
                    onChange={() => handleCheckbox(key, value)}
                  />
                  {String(value)} ({count})
                </label>
              ))
            ) : (
              <div className={styles.emptyNote}>Немає параметрів</div>
            ))}
        </div>
      );
    },
    [availableFilters, sections, filters, handleCheckbox, renderSectionHeading]
  );

  return (
    <div
      className={`${styles.filterSidebar} ${isOpen ? styles.open : ""} ${
        sheetMode ? styles.sheetMode : ""
      }`}
      id="filter-sidebar-mobile"
    >
      <div className={styles.filterHeader}>
        {!sheetMode && (
          <button
            type="button"
            className={styles.closeIcon}
            onClick={onClose}
            aria-label="Закрити фільтри"
          >
            ×
          </button>
        )}
      </div>

      {/* Категорії */}
      <div className={styles.filterSection}>
        <h4
          onClick={() =>
            setSections((prev) => ({
              ...prev,
              categories: !prev.categories,
            }))
          }
        >
          {renderSectionHeading("categories")}
        </h4>

        {sections.categories &&
          (availableCategories.length > 0 ? (
            availableCategories.map((cat) =>
              cat ? (
                <label key={cat}>
                  <input
                    type="checkbox"
                    checked={(filters.categories || []).includes(cat)}
                    onChange={() => {
                      const cur = filters.categories ?? [];
                      const next = cur.includes(cat)
                        ? cur.filter((c) => c !== cat)
                        : [...cur, cat];
                      onFilterChange({ categories: next });
                    }}
                  />
                  {cat}
                  {categoryCounts[cat] !== undefined && (
                    <span style={{ color: "#666" }}>
                      ({categoryCounts[cat]})
                    </span>
                  )}
                </label>
              ) : null
            )
          ) : (
            <div className={styles.emptyNote}>Немає параметрів</div>
          ))}
      </div>

      {/* Масивні стандартні фільтри */}
      {renderArraySection("kinds")}
      {renderArraySection("types")}
      {renderArraySection("purposes")}
      {renderArraySection("manufacturers")}
      {renderArraySection("countries")}
      {renderArraySection("weights")}

      {/* РОЗШИРЕНІ ФІЛЬТРИ */}
      {shouldShowAdvanced && (
        <>
          {renderArraySection("feedClasses")}
          {renderArraySection("animalAges")}
          {renderArraySection("breeds")}
          {renderArraySection("mainIngredients")}

          {/* Трі-стейт */}
          {BOOLEAN_FILTER_KEYS
            .filter(
              (key) => booleanFilterAvailability[key] || filters[key] != null
            )
            .map((key) => (
              <div key={key} className={styles.filterSection}>
                <h4
                  onClick={() =>
                    setSections((prev) => ({
                      ...prev,
                      [key]: !prev[key],
                    }))
                  }
                >
                  {renderSectionHeading(key)}
                </h4>

                {sections[key] && (
                  <div className={styles.triStateGroup}>
                    <label>
                      <input
                        type="radio"
                        name={`tri-${key}`}
                        checked={filters[key] == null}
                        onChange={() => handleTriState(key, null)}
                      />
                      Будь-які
                    </label>

                    <label>
                      <input
                        type="radio"
                        name={`tri-${key}`}
                        checked={filters[key] === true}
                        onChange={() => handleTriState(key, true)}
                      />
                      Так
                    </label>

                    <label>
                      <input
                        type="radio"
                        name={`tri-${key}`}
                        checked={filters[key] === false}
                        onChange={() => handleTriState(key, false)}
                      />
                      Ні
                    </label>
                  </div>
                )}
              </div>
            ))}
        </>
      )}

      {/* Ціна */}
      <div className={styles.filterSection}>
        <h4
          onClick={() =>
            setSections((prev) => ({
              ...prev,
              priceRange: !prev.priceRange,
            }))
          }
        >
          {renderSectionHeading("priceRange")}
        </h4>

        {sections.priceRange && (
          <div className={styles.priceSliderContainer}>
            <div className={styles.priceInputs}>
              <input
                type="number"
                value={priceInput[0]}
                onChange={(e) =>
                  handleSliderChange([+e.target.value, priceInput[1]])
                }
              />
              <span>-</span>
              <input
                type="number"
                value={priceInput[1]}
                onChange={(e) =>
                  handleSliderChange([priceInput[0], +e.target.value])
                }
              />
            </div>

            <Slider
              range
              min={DEFAULT_PRICE_RANGE.min}
              max={DEFAULT_PRICE_RANGE.max}
              value={priceInput}
              onChange={handleSliderChange}
              onAfterChange={handleSliderAfterChange}
              trackStyle={[{ backgroundColor: "#4caf50" }]}
              handleStyle={[
                { borderColor: "#4caf50", backgroundColor: "#4caf50" },
                { borderColor: "#4caf50", backgroundColor: "#4caf50" },
              ]}
            />
          </div>
        )}
      </div>

      {!hideInternalReset && (
        <div className={styles.resetButtonContainer}>
          <button
            type="button"
            onClick={resetAllFilters}
            className={styles.resetButton}
          >
            Скинути фільтри
          </button>
        </div>
      )}
    </div>
  );
};

export default FilterSidebar;
